import {Component, OnDestroy, OnInit} from '@angular/core';
import {RouterLink} from '@angular/router';
import {DecimalPipe, NgClass, NgForOf, NgIf} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatCardModule} from '@angular/material/card';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatSelectModule} from '@angular/material/select';
import {MatTooltipModule} from '@angular/material/tooltip';
import {combineLatest, Subscription} from 'rxjs';

import {
  EvaluationGame,
  EvaluationSeason,
  EvaluationState,
  STATE_LABELS,
} from '../../model/evaluation/evaluation';
import {EvaluationsService} from '../../services/evaluations.service';
import {DeclarationState, declarationLabel, declarationState} from '../declaration';
import {EvaluationExportService} from '../export/evaluation-export.service';
import {computeReward, pendingCriteria, readyToRelease} from '../reward';

interface GameRow {
  game: EvaluationGame;
  pending: number;
  total: number;
  blocked: boolean;
  ready: boolean;
  selected: boolean;
  /** Operated camera: what the producer said, and whether anybody checked. */
  declared: string;
  declaration: DeclarationState;
}

interface ProducerSummary {
  name: string;
  /**
   * The StayLive channel, which is what a production company actually is —
   * the rows are grouped by name because that is what an admin reads, but
   * anything that fetches a production's own data goes by this.
   */
  channelId: number;
  games: number;
  pending: number;
  toRelease: number;
  /** Out with the production company, waiting for them. */
  released: number;
  /** They sent it back — the association's turn again. */
  returned: number;
  approved: number;
  toConfirm: number;
  disputed: number;
  total: number;
}

/**
 * The whole season on one screen, which is how the work actually gets planned:
 * an admin clears one production company at a time, and the release is a
 * deliberate act on a batch rather than something that happens by itself.
 *
 * The amounts are provisional until every criterion has a value — a game with
 * open items shows what it would pay, marked as such, because that is what
 * tells you whether a disputed criterion is worth arguing about.
 */
@Component({
  selector: 'app-evaluation-season-overview',
  templateUrl: './season-overview.component.html',
  styleUrls: ['./season-overview.component.scss'],
  standalone: true,
  imports: [
    RouterLink,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
    MatTooltipModule,
    DecimalPipe,
    NgClass,
    NgForOf,
    NgIf,
  ]
})
export class EvaluationSeasonOverviewComponent implements OnInit, OnDestroy {

  readonly stateLabels = STATE_LABELS;
  readonly states: EvaluationState[] = ['toDecide', 'toApprove', 'returned', 'approved'];
  readonly declarations: { value: DeclarationState; label: string }[] = [
    {value: 'toConfirm', label: 'Camera to confirm'},
    {value: 'disputed', label: 'Camera disputed'},
    {value: 'confirmed', label: 'Camera confirmed'},
    {value: 'none', label: 'Camera declared as none'},
    {value: 'undeclared', label: 'Camera not declared'},
  ];

  season: EvaluationSeason;
  rows: GameRow[] = [];
  visible: GameRow[] = [];
  producers: string[] = [];
  summaries: ProducerSummary[] = [];

  producerFilter = '';
  stateFilter: EvaluationState | '' = '';
  declarationFilter: DeclarationState | '' = '';
  busy = false;
  /** The production whose workbook is being built, so only its button spins. */
  exporting: number | null = null;
  exportError = '';

  private all: EvaluationGame[] = [];
  private subscriptions = new Subscription();

  constructor(
    private evaluations: EvaluationsService,
    private exports: EvaluationExportService,
  ) {
  }

  ngOnInit(): void {
    this.subscriptions.add(
      combineLatest([this.evaluations.season(), this.evaluations.games()])
        .subscribe(([season, games]) => {
          this.season = season;
          this.all = games ?? [];
          this.rebuild();
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private rebuild(): void {
    if (!this.season) {
      return;
    }
    const chosen = new Set(this.rows.filter(row => row.selected).map(row => row.game.videoId));
    this.rows = this.all.map(game => {
      // No subcollection reads here: an admin's rulings are folded into the
      // game document as they are made, so one query answers the whole table.
      const reward = computeReward(game, this.season);
      return {
        game,
        pending: pendingCriteria(game, this.season).length,
        total: game.reward?.total ?? reward.total,
        blocked: reward.blocked,
        ready: readyToRelease(game, this.season),
        selected: chosen.has(game.videoId),
        declared: declarationLabel(game),
        declaration: declarationState(game),
      };
    });
    this.producers = [...new Set(this.all.map(game => game.producerName))].sort();
    this.applyFilters();
    this.summarise();
  }

  applyFilters(): void {
    this.visible = this.rows.filter(row =>
      (!this.producerFilter || row.game.producerName === this.producerFilter)
      && (!this.stateFilter || row.game.state === this.stateFilter)
      && (!this.declarationFilter || row.declaration === this.declarationFilter));
  }

  /** Only a settled broadcast that is still the association's can go out. */
  releasable(row: GameRow): boolean {
    return row.ready && (row.game.state === 'toDecide' || row.game.state === 'returned');
  }

  private summarise(): void {
    const byProducer = new Map<string, ProducerSummary>();
    this.rows.forEach(row => {
      const summary = byProducer.get(row.game.producerName) ?? {
        name: row.game.producerName,
        channelId: row.game.channelId,
        games: 0, pending: 0, toRelease: 0, released: 0, returned: 0,
        approved: 0, toConfirm: 0, disputed: 0, total: 0,
      };
      summary.games += 1;
      summary.pending += row.pending;
      // The same test the release button uses, rather than a second reading
      // of it: a returned broadcast that has been re-ruled is releasable too,
      // and counting only `toDecide` made the summary say nothing was ready
      // while the button below happily released five.
      if (this.releasable(row)) {
        summary.toRelease += 1;
      }
      if (row.game.state === 'toApprove') {
        summary.released += 1;
      }
      if (row.game.state === 'returned') {
        summary.returned += 1;
      }
      if (row.game.state === 'approved') {
        summary.approved += 1;
        summary.total += row.total;
      }
      if (row.declaration === 'toConfirm') {
        summary.toConfirm += 1;
      }
      if (row.declaration === 'disputed') {
        summary.disputed += 1;
      }
      byProducer.set(row.game.producerName, summary);
    });
    this.summaries = [...byProducer.values()].sort((a, b) => b.pending - a.pending);
  }

  get selected(): GameRow[] {
    return this.rows.filter(row => row.selected);
  }

  get selectedReleasable(): GameRow[] {
    return this.selected.filter(row => this.releasable(row));
  }

  toggleAll(checked: boolean): void {
    this.visible.forEach(row => row.selected = checked && this.releasable(row));
  }

  async releaseSelected(): Promise<void> {
    const rows = this.selectedReleasable;
    if (!rows.length) {
      return;
    }
    this.busy = true;
    try {
      await this.evaluations.release(rows.map(row => row.game));
      rows.forEach(row => row.selected = false);
    } finally {
      this.busy = false;
    }
  }

  filterTo(producer: string): void {
    this.producerFilter = producer;
    this.applyFilters();
  }

  // --- the operated camera -------------------------------------------------

  /**
   * Confirmed here, one game at a time, without a trip to the detail screen —
   * but the link is right there for the games where an admin wants to watch
   * the recording before putting their name to 825 or 1650 crowns. Only a
   * claim that pays ever offers this: `toConfirm` excludes a none, which is
   * finished the moment the producer declares it.
   *
   * The other answer, disputing it, is deliberately *not* here: it takes a
   * reason the producer will read, and it is made after watching, which is
   * the detail screen's job.
   */
  async confirm(row: GameRow): Promise<void> {
    this.busy = true;
    try {
      await this.evaluations.confirmDeclaration(row.game.videoId);
    } finally {
      this.busy = false;
    }
  }

  // --- the invoice ---------------------------------------------------------

  /**
   * One production's approved season, as a spreadsheet.
   *
   * This is the answer to the only question the detail screens cannot answer
   * at the end of a season: *what are we paying you, and for what*. Approved
   * games only — anything earlier is still being argued about, and a
   * provisional amount in a spreadsheet is a provisional amount somebody will
   * quote back at you.
   *
   * It reads the whole season the screen already has and fetches only the two
   * subcollections per game, so pressing it does not re-download the table.
   */
  async exportProducer(summary: ProducerSummary, event?: Event): Promise<void> {
    // The summary row itself filters the table; the button must not do both.
    event?.stopPropagation();
    this.exporting = summary.channelId;
    this.exportError = '';
    try {
      await this.exports.exportProducer(summary.channelId, this.all, this.season);
    } catch (error: any) {
      this.exportError = `${summary.name}: ${error?.message ?? 'the export failed'}`;
    } finally {
      this.exporting = null;
    }
  }
}
